import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Users, MessageCircle, ExternalLink, X, Pencil, Trash2, Crown, Link2, Send } from 'lucide-react';
import { navigate, safeFetch } from './App';
import { memoryCache } from './cacheSafeFetch';

export const GroupView = ({ group, onClose, chatId, zone, showHeader = true }) => {
    if (!group) return null;

    const isOwner = group.owner === true || group.owner === chatId;
    const groupId = group.group_id || group.id;

    const [currentGroup, setCurrentGroup] = useState(group);
    const [isEditing, setIsEditing] = useState(false);
    const [edited, setEdited] = useState(() => ({
        name: group.name || group.group_name || '',
        description: group.description || '',
        chat: Array.isArray(group.chats) && group.chats.length > 0
            ? { name: group.chats[0].name || '', url: group.chats[0].url || '' }
            : { name: '', url: '' },
    }));
    const [isSaving, setIsSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleBack = () => {
        if (onClose) {
            onClose();
        } else {
            window.history.back();
        }
    };

    const handleSave = async () => {
        if (!groupId || !zone) return;
        setIsSaving(true);
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-group',
                chatId,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        group_id: groupId,
                        zone_id: zone,
                        name: edited.name,
                        description: edited.description,
                        chats: edited.chat.url.trim() ? [edited.chat] : [],
                    })
                }
            );
            if (!res.ok) throw new Error('update failed');
            const updated = {
                ...currentGroup,
                name: edited.name,
                group_name: edited.name,
                description: edited.description,
                chats: edited.chat.url.trim() ? [edited.chat] : [],
            };
            setCurrentGroup(updated);
            setIsEditing(false);
            delete memoryCache[`get-zone-groups-${zone}-${chatId}`];
            alert('הקבוצה עודכנה בהצלחה');
        } catch (e) {
            console.error('update-group error', e);
            alert('שגיאה בעדכון הקבוצה');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!groupId || !zone) return;
        setIsDeleting(true);
        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-group',
                chatId,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        group_id: groupId,
                        zone_id: zone,
                    })
                }
            );
            if (!res.ok) throw new Error('delete failed');
            delete memoryCache[`get-zone-groups-${zone}-${chatId}`];
            alert('הקבוצה נמחקה בהצלחה');
            if (onClose) onClose();
            if (zone) navigate('zone-details', `zone=${zone}&tab=groups`);
        } catch (e) {
            console.error('delete-group error', e);
            alert('שגיאה במחיקת הקבוצה');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const groupName = currentGroup.name || currentGroup.group_name;
    const hasImage = currentGroup.image_key || currentGroup.image || currentGroup.cover_image;
    const imageUrl = currentGroup.image_key || currentGroup.image || currentGroup.cover_image;

    return (
        <div className="min-h-screen bg-gradient-to-br from-surface-muted via-white to-brand-50/20" style={{ direction: 'rtl' }}>
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                {/* Background with image or gradient */}
                {hasImage ? (
                    <div className="relative h-56 sm:h-72">
                        <img
                            src={imageUrl}
                            alt={groupName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                    </div>
                ) : (
                    <div className="relative h-48 sm:h-56 bg-gradient-to-br from-brand-500 via-brand-500 to-brand-600">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(255,255,255,0.1),transparent_50%)]" />
                    </div>
                )}

                {/* Minimal back button - just an icon */}
                {showHeader && (
                    <motion.button
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        onClick={handleBack}
                        className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-ink flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
                    >
                        <ArrowLeft size={20} strokeWidth={2.5} className="rotate-180" />
                    </motion.button>
                )}

                {/* Hero content overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="container max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="flex items-end gap-4"
                        >
                            {/* Group icon */}
                            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-xl border border-white/30 flex-shrink-0">
                                <Users size={32} className="text-white" strokeWidth={2} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 drop-shadow-lg">
                                    {groupName}
                                </h1>
                                {isOwner && (
                                    <div className="flex items-center gap-1.5 text-white/90">
                                        <Crown size={14} className="fill-warning-500 text-warning-500" />
                                        <span className="text-sm font-medium">הקבוצה שלך</span>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container max-w-4xl mx-auto px-4 py-6 pb-32 -mt-4 relative z-10">
                {/* Owner Actions */}
                {isOwner && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-3 justify-end mb-6"
                    >
                        <button
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-ink hover:bg-brand-50 hover:text-brand-600 transition-all shadow-md border border-line-subtle font-semibold"
                        >
                            <Pencil size={16} />
                            <span>עריכה</span>
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-ink hover:bg-danger-50 hover:text-danger-600 transition-all shadow-md border border-line-subtle font-semibold"
                        >
                            <Trash2 size={16} />
                            <span>מחיקה</span>
                        </button>
                    </motion.div>
                )}

                {/* Categories */}
                {currentGroup.categories && Array.isArray(currentGroup.categories) && currentGroup.categories.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        className="flex flex-wrap gap-2 mb-6"
                    >
                        {currentGroup.categories.map((cat, idx) => (
                            <motion.span
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.3 + idx * 0.05 }}
                                className="px-4 py-2 bg-gradient-to-br from-brand-100 to-brand-100 text-brand-700 rounded-xl text-sm font-semibold border border-brand-200/50 shadow-sm"
                            >
                                {cat}
                            </motion.span>
                        ))}
                    </motion.div>
                )}

                {/* Description Card */}
                {currentGroup.description && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, type: "spring", stiffness: 100 }}
                        className="bg-white rounded-2xl p-6 mb-6 shadow-lg border border-line-subtle/80"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-500 flex items-center justify-center shadow-md">
                                <MessageCircle size={20} className="text-white" strokeWidth={2} />
                            </div>
                            <h2 className="text-lg font-bold text-ink-strong">אודות הקבוצה</h2>
                        </div>
                        <p className="text-ink-muted leading-relaxed whitespace-pre-wrap text-base">
                            {currentGroup.description}
                        </p>
                    </motion.div>
                )}

                {/* Chat Links */}
                {currentGroup.chats && Array.isArray(currentGroup.chats) && currentGroup.chats.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 100 }}
                        className="bg-white rounded-2xl p-6 shadow-lg border border-line-subtle/80"
                    >
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info-500 to-brand-500 flex items-center justify-center shadow-md">
                                <Link2 size={20} className="text-white" strokeWidth={2} />
                            </div>
                            <h2 className="text-lg font-bold text-ink-strong">הצטרפות לקבוצה</h2>
                        </div>
                        <div className="space-y-3">
                            {currentGroup.chats.map((chat, idx) => {
                                const isTelegram = chat.url.includes('t.me') || chat.url.includes('telegram');
                                const isWhatsApp = chat.url.includes('wa.me') || chat.url.includes('whatsapp');

                                const getGradient = () => {
                                    if (isWhatsApp) return 'from-success-500 to-success-600';
                                    if (isTelegram) return 'from-info-500 to-info-600';
                                    return 'from-brand-500 to-brand-600';
                                };

                                const getIcon = () => {
                                    if (isWhatsApp) return (
                                        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                        </svg>
                                    );
                                    if (isTelegram) return (
                                        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                                            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                        </svg>
                                    );
                                    return <ExternalLink size={20} />;
                                };

                                const getLabel = () => {
                                    if (isWhatsApp) return 'WhatsApp';
                                    if (isTelegram) return 'Telegram';
                                    return 'קישור';
                                };

                                return (
                                    <motion.a
                                        key={idx}
                                        href={chat.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.5 + idx * 0.1 }}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className={`flex items-center justify-between gap-4 p-4 rounded-xl bg-gradient-to-l ${getGradient()} text-white shadow-lg hover:shadow-xl transition-all group`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                                {getIcon()}
                                            </div>
                                            <div>
                                                <p className="font-bold text-base">{chat.name || getLabel()}</p>
                                                <p className="text-sm text-white/80">{getLabel()}</p>
                                            </div>
                                        </div>
                                        <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center group-hover:bg-white/30 transition-colors">
                                            <Send size={18} className="rotate-180" />
                                        </div>
                                    </motion.a>
                                );
                            })}
                        </div>
                    </motion.div>
                )}

                {/* Empty state when no description and no chats */}
                {!currentGroup.description && (!currentGroup.chats || currentGroup.chats.length === 0) && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-12 bg-white rounded-2xl shadow-lg border border-line-subtle"
                    >
                        <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-100 flex items-center justify-center">
                            <Users size={36} className="text-brand-500" />
                        </div>
                        <h3 className="text-lg font-bold text-ink-strong mb-2">אין פרטים נוספים</h3>
                        <p className="text-ink-subtle text-sm">
                            {isOwner ? 'לחץ על עריכה כדי להוסיף תיאור וקישורים' : 'הקבוצה עדיין לא הוסיפה פרטים נוספים'}
                        </p>
                    </motion.div>
                )}
            </div>

            {/* Edit Modal */}
            <AnimatePresence>
                {isEditing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsEditing(false)} />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-3xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl"
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 pb-4 border-b border-line-subtle flex-shrink-0">
                                <h3 className="text-xl font-bold text-ink-strong">עריכת קבוצה</h3>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="w-10 h-10 rounded-xl bg-surface-muted hover:bg-surface-sunken flex items-center justify-center transition-colors"
                                >
                                    <X size={20} className="text-ink-muted" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="space-y-5 overflow-y-auto p-6 flex-1">
                                <div>
                                    <label className="block text-sm font-semibold text-ink mb-2">שם הקבוצה</label>
                                    <input
                                        type="text"
                                        value={edited.name}
                                        onChange={(e) => setEdited({ ...edited, name: e.target.value })}
                                        className="w-full border-2 border-line focus:border-brand-400 rounded-xl p-3.5 text-right bg-surface-muted focus:bg-white text-ink-strong placeholder:text-ink-subtle transition-all outline-none"
                                        placeholder="הזן שם לקבוצה"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-ink mb-2">תיאור</label>
                                    <textarea
                                        value={edited.description}
                                        onChange={(e) => setEdited({ ...edited, description: e.target.value })}
                                        rows={4}
                                        className="w-full border-2 border-line focus:border-brand-400 rounded-xl p-3.5 text-right bg-surface-muted focus:bg-white text-ink-strong placeholder:text-ink-subtle transition-all outline-none resize-none"
                                        placeholder="ספר על הקבוצה..."
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-ink mb-2">קישור צ׳אט</label>
                                    <div className="space-y-3">
                                        <input
                                            type="text"
                                            placeholder="שם הצ׳אט (לדוגמה: קבוצת וואטסאפ)"
                                            value={edited.chat.name || ''}
                                            onChange={(e) => setEdited({ ...edited, chat: { ...edited.chat, name: e.target.value } })}
                                            className="w-full border-2 border-line focus:border-brand-400 rounded-xl p-3.5 text-right bg-surface-muted focus:bg-white text-ink-strong placeholder:text-ink-subtle transition-all outline-none"
                                        />
                                        <input
                                            type="text"
                                            placeholder="https://..."
                                            value={edited.chat.url || ''}
                                            onChange={(e) => setEdited({ ...edited, chat: { ...edited.chat, url: e.target.value } })}
                                            className="w-full border-2 border-line focus:border-brand-400 rounded-xl p-3.5 text-right bg-surface-muted focus:bg-white text-ink-strong placeholder:text-ink-subtle transition-all outline-none font-mono text-sm"
                                            dir="ltr"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="flex items-center gap-3 p-6 pt-4 border-t border-line-subtle flex-shrink-0">
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="flex-1 px-4 py-3 rounded-xl bg-surface-muted hover:bg-surface-sunken text-ink font-semibold transition-colors"
                                >
                                    ביטול
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white font-bold shadow-lg disabled:opacity-60 transition-all"
                                >
                                    {isSaving ? 'שומר...' : 'שמור שינויים'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    >
                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            className="relative bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl text-center"
                        >
                            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-danger-100 to-danger-100 flex items-center justify-center">
                                <Trash2 size={28} className="text-danger-500" />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong mb-2">מחיקת קבוצה</h3>
                            <p className="text-ink-muted mb-6">האם אתה בטוח שברצונך למחוק את הקבוצה? פעולה זו לא ניתנת לביטול.</p>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setShowDeleteConfirm(false)}
                                    className="flex-1 px-4 py-3 rounded-xl bg-surface-muted hover:bg-surface-sunken text-ink font-semibold transition-colors"
                                >
                                    ביטול
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 hover:from-danger-600 hover:to-danger-700 text-white font-bold shadow-lg disabled:opacity-60 transition-all"
                                >
                                    {isDeleting ? 'מוחק...' : 'מחק'}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
